import { cn } from "@/modules/lib/utils"
import { Text } from "@/components/ui/baseUi/text"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { InputGroup, InputGroupAddon } from "@/components/ui/baseUi/input-group"
import { CheckIcon, PencilIcon, PlusIcon, SearchIcon, Trash2Icon, XIcon } from "lucide-react"
import { Suspense, useCallback, useEffect, useRef, useState } from "react"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { useTemplateCategries, useTemplateCategriesActions } from "@/modules/template/stores/useTemplateCategriesStore"
import { TemplateFormCategoryValidationSchema } from "@/modules/template/forms/templateFormCategoryValidation"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { Button } from "@/components/ui/baseUi/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/baseUi/dialog"
import {
  useCreateTemplateCategory,
  useDeleteTemplateCategory,
  useTemplateCategoriesInfinite,
  useUpdateTemplateCategory,
} from "@/modules/template/hooks/useTemplateCategory"
import { toast } from "sonner"
import { errorToastClassName, successToastClassName } from "@/components/ui/baseUi/sonner"
import type { TemplateCategoryEntity } from "@/modules/template/entities/templateCategoryEntity"
import { ErrorBoundary } from "@sentry/react"
import { ErrorView } from "@/components/ui/layout/error/errorView"
import { AlertIconLight } from "@/assets/images/svgIcons/alertIcon"
import { CategorieIllustration } from "@/assets/images/svgIcons/illustrations/categorieIllustration"
import { word } from "@/modules/core/utils/i18n"

type TemplateCategoriesViewProps = {
  className?: string
} & React.ComponentProps<"div">

export function TemplateCategoriesView({ className, ...props }: TemplateCategoriesViewProps) {
  return (
    <div className={cn("flex flex-1 flex-col gap-4 pt-18 pb-6", className)} {...props}>
      <div className="flex flex-1 flex-col gap-8">
        <Text fontStyle="TL" className="px-6">
          {word("global.category", { count: 2 })}
        </Text>
        <div className="flex flex-1 flex-col gap-2">
          <TemplateCategoriesSearch className="px-6" />
          <TemplateCategoriesCreate className="pr-4 pl-6" />
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner className="flex-1 px-6" />}>
              <TemplateCategoriesList className="pr-4 pl-6" />
            </Suspense>
          </ErrorBoundary>
        </div>
      </div>
    </div>
  )
}

type TemplateCategoriesListProps = {
  className?: string
} & React.ComponentProps<"div">

function TemplateCategoriesList({ className, ...props }: TemplateCategoriesListProps) {
  const searchTerms = useTemplateCategries()
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useTemplateCategoriesInfinite({
    searchTerm: searchTerms,
  })

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      observer.disconnect()
    }
  }, [handleIntersection])

  if (data.length === 0 && searchTerms) {
    return (
      <div className={cn("flex w-full flex-col items-center justify-center gap-2 py-8", className)} {...props}>
        <CategorieIllustration color="currentColor" />
        <Text fontStyle="LL">{word("template.categories.noCategoriesFound")}</Text>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className={cn("flex w-full flex-col items-center justify-center gap-2 py-8", className)} {...props}>
        <CategorieIllustration color="currentColor" />
        <Text fontStyle="LL">{word("template.categories.noCategories")}</Text>
      </div>
    )
  }

  return (
    <div className={cn("flex w-full flex-col gap-2", className)} {...props}>
      {data.map((item) => (
        <TemplateCategoriesListItem key={item.id} item={item} />
      ))}
      {(hasNextPage || isFetchingNextPage) && (
        <div ref={loadMoreRef} className="flex w-full items-center justify-center py-4">
          <LoadingSpinner className="animate-spin" />
        </div>
      )}
    </div>
  )
}

type TemplateCategoriesListItemsProps = {
  item: TemplateCategoryEntity
  className?: string
} & React.ComponentProps<"div">

function TemplateCategoriesListItem({ item, className, ...props }: TemplateCategoriesListItemsProps) {
  const language = useLanguage()
  const { mutateAsync: updateTemplateCategory } = useUpdateTemplateCategory()
  const { mutateAsync: deleteTemplateCategory, isPending: isDeletingTemplateCategory } = useDeleteTemplateCategory()
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const defaultValues = {
    name: item.name,
  }
  const form = useAppForm({
    defaultValues: {
      ...defaultValues,
    },
    validators: {
      onSubmit: TemplateFormCategoryValidationSchema(language),
      onBlur: TemplateFormCategoryValidationSchema(language),
    },
    onSubmit: async () => {
      await updateTemplateCategory({ id: item.id, editTemplateCategoryInput: { name: form.state.values.name } })
        .then(() => {
          toast.success(word("template.categories.toast.updated.success"), {
            classNames: successToastClassName(),
          })
          setIsEditing(false)
          form.reset({
            ...defaultValues,
          })
        })
        .catch(() => {
          toast.error(word("template.categories.toast.updated.error"), {
            classNames: errorToastClassName(),
          })
        })
    },
  })

  if (isEditing) {
    return (
      <div
        className={cn(
          "flex flex-row items-center justify-between gap-2 border-b border-surface-border-gray-subtle py-0.5",
          className,
        )}
        {...props}
      >
        <form.AppField name="name">
          {(field) => (
            <field.Field className="py-2">
              <field.Content>
                <field.Input name="name" />
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>
        <div className="flex flex-row items-center gap-2 py-2">
          <form.Subscribe selector={(state) => [state.isSubmitting, state.isPristine, state.isDefaultValue]}>
            {([isSubmitting, isPristine, isDefaultValue]) => (
              <>
                <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                  <DialogTrigger
                    render={
                      <Button
                        variant="negativeSecondary"
                        size="iconLg"
                        disabled={isSubmitting || isDeletingTemplateCategory}
                      >
                        <Trash2Icon />
                      </Button>
                    }
                  />
                  <DialogContent className="max-w-lg gap-6 p-8">
                    <DialogHeader className="flex flex-col items-center justify-center gap-6 pt-4 text-center">
                      <DialogTitle className="flex flex-col items-center justify-center gap-1 title-large!">
                        <AlertIconLight color="currentColor" />
                        {word("template.categories.dialog.delete.title")}
                      </DialogTitle>
                      <DialogDescription className="body-medium!">
                        {word("template.categories.dialog.delete.description")}
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        size="lg"
                        className="flex-1"
                        onClick={() => {
                          setIsDeleteDialogOpen(false)
                        }}
                        disabled={isDeletingTemplateCategory}
                      >
                        {word("global.cancel")}
                      </Button>
                      <Button
                        variant="negativePrimary"
                        size="lg"
                        className="flex-1"
                        onClick={() => {
                          void deleteTemplateCategory(item.id)
                            .then(() => {
                              toast.success(word("template.categories.toast.deleted.success"), {
                                classNames: successToastClassName(),
                              })
                              setIsDeleteDialogOpen(false)
                            })
                            .catch(() => {
                              toast.error(word("template.categories.toast.deleted.error"), {
                                classNames: errorToastClassName(),
                              })
                            })
                        }}
                        disabled={isDeletingTemplateCategory}
                      >
                        {isDeletingTemplateCategory ? (
                          <LoadingSpinner className="animate-spin" size={16} />
                        ) : (
                          word("global.delete")
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <Button
                  variant="ghost"
                  size="iconLg"
                  onClick={() => {
                    setIsEditing(false)
                  }}
                  disabled={isSubmitting || isDeletingTemplateCategory}
                >
                  <XIcon />
                </Button>

                <Button
                  variant="positiveSecondary"
                  size="iconLg"
                  onClick={() => void form.handleSubmit()}
                  disabled={isSubmitting || isDeletingTemplateCategory || isPristine || isDefaultValue}
                >
                  {isSubmitting ? <LoadingSpinner className="animate-spin" /> : <CheckIcon />}
                </Button>
              </>
            )}
          </form.Subscribe>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        "flex flex-row items-center justify-between border-b border-surface-border-gray-subtle py-0.5",
        className,
      )}
      {...props}
    >
      <Text fontStyle="BL" className="">
        {item.name}
      </Text>
      <Button
        variant="ghost"
        size="iconLg"
        onClick={() => {
          setIsEditing(!isEditing)
        }}
      >
        <PencilIcon />
      </Button>
    </div>
  )
}

type TemplateCategoriesSearchProps = {
  className?: string
} & React.ComponentProps<"div">

function TemplateCategoriesSearch({ className, ...props }: TemplateCategoriesSearchProps) {
  const searchTerms = useTemplateCategries()
  const { setSearchTerms } = useTemplateCategriesActions()

  const defaultValues = {
    searchTerms: searchTerms,
  }

  const form = useAppForm({
    defaultValues: {
      ...defaultValues,
    },
  })

  return (
    <form.AppField
      name="searchTerms"
      validators={{
        onChangeAsyncDebounceMs: 350,
        onChangeAsync: async ({ value }) => {
          await new Promise((resolve) => setTimeout(resolve, 0))
          setSearchTerms(value)
        },
      }}
    >
      {(field) => (
        <field.Field className={cn(className)} {...props}>
          <field.Content>
            <InputGroup className="h-12">
              <field.InputGroupInput
                name="searchTerms"
                placeholder={word("template.categories.form.search.placeholder")}
              />
              <InputGroupAddon align="inline-start">
                <SearchIcon />
              </InputGroupAddon>
              {field.state.value && (
                <InputGroupAddon align="inline-end">
                  <XIcon
                    className="size-4 cursor-pointer"
                    onClick={() => {
                      field.handleChange("")
                    }}
                  />
                </InputGroupAddon>
              )}
            </InputGroup>

            <field.Message />
          </field.Content>
        </field.Field>
      )}
    </form.AppField>
  )
}

type TemplateCategoriesCreateProps = {
  className?: string
} & React.ComponentProps<"div">

function TemplateCategoriesCreate({ className, ...props }: TemplateCategoriesCreateProps) {
  const language = useLanguage()
  const { mutateAsync: createTemplateCategory } = useCreateTemplateCategory()

  const defaultValues = {
    name: "",
  }

  const form = useAppForm({
    defaultValues: {
      ...defaultValues,
    },
    validators: {
      onSubmit: TemplateFormCategoryValidationSchema(language),
      onBlur: TemplateFormCategoryValidationSchema(language),
    },
    onSubmit: async () => {
      await createTemplateCategory({ name: form.state.values.name })
        .then(() => {
          toast.success(word("template.categories.toast.created.success"), {
            classNames: successToastClassName(),
          })
          form.reset({
            ...defaultValues,
          })
        })
        .catch(() => {
          toast.error(word("template.categories.toast.created.error"), {
            classNames: errorToastClassName(),
          })
        })
    },
  })
  return (
    <div className={cn("flex w-full gap-2 bg-surface-background-secondary", className)} {...props}>
      <form.AppField name="name">
        {(field) => (
          <field.Field className="py-2">
            <field.Content>
              <field.Input
                name="name"
                placeholder={word("template.categories.form.create.placeholder")}
                className="h-12 bg-interactive-background-neutral-default"
              />
              <field.Message />
            </field.Content>
          </field.Field>
        )}
      </form.AppField>
      <form.Subscribe selector={(state) => [state.isSubmitting]}>
        {([isSubmitting]) => (
          <Button
            variant="outline"
            className="mt-2 self-start"
            size="iconLg"
            onClick={() => void form.handleSubmit()}
            disabled={isSubmitting}
          >
            {isSubmitting ? <LoadingSpinner className="animate-spin" size={16} /> : <PlusIcon size={16} />}
          </Button>
        )}
      </form.Subscribe>
    </div>
  )
}
